import { forwardRef } from 'react'
import type { Dashboard } from '../types'
import { InsightChart } from '../charts/InsightChart'
import { ChartErrorBoundary } from '../charts/ChartErrorBoundary'
import { PMark, Wordmark } from './Logo'
import { TENANT } from '../tenant'
import { formatDay, todayIso } from '../format'
import { OUT_OF_SCOPE_INFO, OUT_OF_SCOPE_TEXT } from '../cardScope'
import { neutralSavedCard } from '../legacySourceText'

// Off-screen export layout: a clean, content-sized 2-column report (no app
// chrome). PNG captures the whole `.export-page`; PDF captures the header and
// each `[data-export-card]` separately and packs them across as many A4 pages
// as needed (see util/exporter.ts), so every card gets room and nothing is
// crammed into one fixed grid.
export const ExportLayout = forwardRef<HTMLDivElement, { dashboard: Dashboard }>(
  function ExportLayout({ dashboard }, ref) {
    const date = formatDay(todayIso(), { long: true })
    return (
      <div className="export-stage" aria-hidden>
        <div className="export-page" ref={ref}>
          <header className="export-head" data-export-header>
            <div>
              <h1>{dashboard.name}</h1>
              <p className="export-date">{date}</p>
            </div>
            <div className="export-brand">
              <PMark size={28} />
              <Wordmark />
            </div>
          </header>

          <div className="export-grid">
            {/* MPD-118: a saved answer in the neutral source wording, also on a card that never refreshes. */}
            {dashboard.cards.map(neutralSavedCard).map((card) => (
              <div className="export-card" data-export-card data-export-kind={card.type} key={card.id}>
                {card.type === 'chart' && card.out_of_scope ? (
                  // MPD-84 N1: the same state as on the board, with no figures.
                  <>
                    <h3>{card.title}</h3>
                    <p className="export-stat">{OUT_OF_SCOPE_TEXT}</p>
                    <p className="export-cap">{OUT_OF_SCOPE_INFO}</p>
                  </>
                ) : card.type === 'chart' ? (
                  <>
                    <h3>{card.title}</h3>
                    {card.last_contract.chart ? (
                      <>
                        <p className="export-cap">{card.last_contract.answer}</p>
                        <ChartErrorBoundary><InsightChart chart={card.last_contract.chart} animate={false} /></ChartErrorBoundary>
                      </>
                    ) : (
                      <p className="export-stat">{card.last_contract.answer}</p>
                    )}
                    {card.last_contract.caveat && <p className="export-cap">{card.last_contract.caveat}</p>}
                    {/* The card's ⓘ notes (MPD-67), written out once as text: the export has no ⓘ. */}
                    {card.last_contract.caveat_info && <p className="export-cap">{card.last_contract.caveat_info}</p>}
                    <p className="export-cap export-meta">
                      {card.last_contract.preliminary && <span>Preliminary · </span>}
                      {card.last_contract.stale
                        ? `Last available data · ${card.last_contract.data_as_of ?? 'date unknown'}`
                        : Number.isFinite(Date.parse(card.pinned_at))
                          ? `Updated ${formatDay(todayIso(new Date(card.pinned_at)))}`
                          : 'Updated date unknown'}
                      {!card.spec && <span> · Saved analysis</span>}
                    </p>
                  </>
                ) : card.type === 'action' ? (
                  <>
                    <h3>{card.heading || 'Actions'}</h3>
                    <ul className="export-actions">
                      {card.items.map((it) => (
                        <li key={it.id}>{it.done ? '☑' : '☐'} {it.text}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <>
                    <h3>{card.heading || 'Heading'}</h3>
                    <p className="export-text">{card.body}</p>
                  </>
                )}
              </div>
            ))}
          </div>

          <footer className="export-foot">Generated with {TENANT.productName} · {date}</footer>
        </div>
      </div>
    )
  },
)
